import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { Heading, Icon, Text } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';
import { useDynamicIcon } from '@lumapps/utils/hooks/useDynamicIcon';
import type { WidgetGlobalStyles } from '@lumapps/widget-styles/types';
import { FlexDirection } from '@lumapps/widget-styles/types/enum';
import { computeHeaderStyles } from '@lumapps/widget-styles/utils/computeHeaderStyles';

import { Header } from '../../types';
import { getHeaderLabel } from './getHeaderLabel';
import { WidgetHeaderWrapper } from './WidgetHeaderWrapper';

import './index.scss';

interface WidgetHeaderProps extends Header {
    /** The widget header ID */
    id: string;
    /** ARIA expanded. */
    'aria-expanded': boolean;
    /** ARIA controls. */
    'aria-controls': string;
    /** Toggles the collapsed property of the widget. Comes from the reducer. */
    onCollapse?(): void;
    /** Parent widget root style */
    parentStyle?: WidgetGlobalStyles;
    /** Header is standalone (no footer & content collapsed) */
    isStandalone?: boolean;
}

const CLASSNAME = 'widget-header';

export const WidgetHeader: React.FC<WidgetHeaderProps> = (props) => {
    const {
        id,
        icon,
        style,
        onCollapse,
        'aria-controls': ariaControls,
        'aria-expanded': ariaExpanded,
        parentStyle,
        isStandalone,
    } = props;
    const { translateKey } = useTranslate();
    const { iconPath } = useDynamicIcon({ icon: icon?.trim() || '' });
    const flexDirection: FlexDirection = style?.wrapper?.flexDirection || FlexDirection.row;
    const label = getHeaderLabel(props, translateKey);

    const { element, block } = useClassnames(CLASSNAME);
    const styles = computeHeaderStyles(style, parentStyle, isStandalone);

    return (
        <div className={block()} style={styles.main}>
            <Heading className={element('heading')} dir="auto" typography="subtitle2">
                <WidgetHeaderWrapper
                    id={id}
                    className={element(
                        'wrapper',
                        {
                            clickable: Boolean(onCollapse),
                            'layout-bottom': flexDirection === FlexDirection.columnReverse,
                            'layout-left': flexDirection === FlexDirection.row,
                            'layout-right': flexDirection === FlexDirection.rowReverse,
                            'layout-top': flexDirection === FlexDirection.column,
                        },
                        ['widget-wrapper'],
                    )}
                    onClick={onCollapse}
                    style={styles.wrapper}
                    aria-expanded={onCollapse ? ariaExpanded : null}
                    aria-controls={onCollapse ? ariaControls : null}
                >
                    {icon && <Icon className={element('icon')} icon={iconPath} style={styles.icon} />}
                    {label && (
                        <Text as="span" className={element('title')} style={styles.label}>
                            {label}
                        </Text>
                    )}
                </WidgetHeaderWrapper>
            </Heading>
        </div>
    );
};
WidgetHeader.displayName = 'WidgetHeader';
